"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Squid = void 0;
/* eslint-disable no-case-declarations */
const types_1 = require("./types");
const HttpAdapter_1 = __importDefault(require("./adapter/HttpAdapter"));
const TokensChains_1 = require("./utils/TokensChains");
const handlers_1 = require("./handlers");
const evm_1 = require("./utils/evm");
const cosmos_1 = require("./utils/cosmos");
const baseUrl = "https://testnet.api.squidrouter.com/";
class Squid extends TokensChains_1.TokensChains {
    constructor(config = {}) {
        super();
        this.handlers = {
            evm: new handlers_1.EvmHandler(),
            cosmos: new handlers_1.CosmosHandler()
        };
        this.initialized = false;
        this.isInMaintenanceMode = false;
        if (!config.integratorId) {
            throw new Error("integratorId required");
        }
        this.httpInstance = new HttpAdapter_1.default({
            baseUrl: config?.baseUrl || baseUrl,
            config,
            headers: {
                "x-integrator-id": config.integratorId
            },
            timeout: config.timeout
        });
        this.config = {
            baseUrl: config?.baseUrl || baseUrl,
            ...config
        };
    }
    setConfig(config) {
        this.httpInstance = new HttpAdapter_1.default({
            baseUrl: config?.baseUrl || baseUrl,
            config,
            headers: {
                "x-integrator-id": config.integratorId || "squid-sdk"
            },
            timeout: config.timeout
        });
        this.config = {
            baseUrl: config?.baseUrl || baseUrl,
            ...config
        };
    }
    async init() {
        const response = await this.httpInstance.get("v2/sdk-info");
        if (response.status != 200) {
            throw new Error("SDK initialization failed");
        }
        this.tokens = response.data.tokens;
        this.chains = response.data.chains;
        this.isInMaintenanceMode = response.data.isInMaintenanceMode;
        this.maintenanceMessage = response.data.maintenanceMessage;
        this.axelarscanURL = response.data.axlScanUrl;
        this.initialized = true;
    }
    // PUBLIC METHODS
    async getStatus(params) {
        const { data, headers } = await this.httpInstance.axios.get("/v1/status", {
            params,
            headers: {
                ...(this.httpInstance.axios.defaults.headers.common &&
                    this.httpInstance.axios.defaults.headers.common),
                ...(params.requestId && { "x-request-id": params.requestId }),
                ...(params.integratorId && { "x-integrator-id": params.integratorId })
            }
        });
        const requestId = headers && "x-request-id" in headers
            ? headers["x-request-id"]
            : undefined;
        const integratorId = headers && "x-integrator-id" in headers
            ? headers["x-integrator-id"]
            : undefined;
        return { ...data, requestId, integratorId };
    }
    async getRoute(params) {
        this.validateInit();
        const { data, headers, status } = await this.httpInstance.post("v2/route", params);
        if (status != 200) {
            throw new Error(data.error);
        }
        const requestId = headers && "x-request-id" in headers
            ? headers["x-request-id"]
            : undefined;
        const integratorId = headers && "x-integrator-id" in headers
            ? headers["x-integrator-id"]
            : undefined;
        return { ...data, requestId, integratorId };
    }
    async executeRoute(data) {
        this.validateInit();
        this.validateTransactionRequest(data.route);
        const fromChain = this.getChainData(data.route.params.fromChain);
        switch (fromChain.chainType) {
            case types_1.ChainType.EVM:
                const evmParams = this.handlers.evm.populateRouteParams(this, data.route.params, data.signer);
                return this.handlers.evm.executeRoute({ data, params: evmParams });
            case types_1.ChainType.COSMOS:
                const cosmosParams = this.handlers.cosmos.populateRouteParams(this, data.route.params);
                return this.handlers.cosmos.executeRoute({
                    data,
                    params: cosmosParams
                });
            default:
                throw new Error(`Method not supported given chain type ${fromChain.chainType}`);
        }
    }
    async isRouteApproved({ route, sender }) {
        this.validateInit();
        this.validateTransactionRequest(route);
        const fromChain = this.getChainData(route.params.fromChain);
        switch (fromChain.chainType) {
            case types_1.ChainType.EVM:
                const params = this.handlers.evm.populateRouteParams(this, route.params);
                return await this.handlers.evm.isRouteApproved({
                    sender,
                    params,
                    target: route.transactionRequest.target
                });
            default:
                throw new Error(`Method not supported given chain type ${fromChain.chainType}`);
        }
    }
    async approveRoute(data) {
        this.validateInit();
        this.validateTransactionRequest(data.route);
        const fromChain = this.getChainData(data.route.params.fromChain);
        switch (fromChain.chainType) {
            case types_1.ChainType.EVM:
                const params = this.handlers.evm.populateRouteParams(this, data.route.params, data.signer);
                return this.handlers.evm.approveRoute({ data, params });
            default:
                throw new Error(`Method not supported given chain type ${fromChain.chainType}`);
        }
    }
    getRawTxHex(data) {
        this.validateInit();
        this.validateTransactionRequest(data.route);
        return this.handlers.evm.getRawTxHex({ ...data });
    }
    async getTokenPrice({ tokenAddress, chainId }) {
        const response = await this.httpInstance.axios.get("/v2/token-price", {
            params: { tokenAddress, chainId }
        });
        return response.data.token.usdPrice;
    }
    async getFromAmount({ fromToken, toAmount, toToken, slippagePercentage = 1.5 }) {
        // if there is an error getting real-time prices,
        // use the price at the time of initialization
        const [fromTokenPrice = fromToken.usdPrice, toTokenPrice = toToken.usdPrice] = await Promise.all([
            this.getTokenPrice({
                chainId: fromToken.chainId,
                tokenAddress: fromToken.address
            }),
            this.getTokenPrice({
                chainId: toToken.chainId,
                tokenAddress: toToken.address
            })
        ]);
        // example fromAmount: 10
        const fromAmount = (toTokenPrice * Number(toAmount ?? 0)) / fromTokenPrice;
        // fromAmount (10) * slippagePercentage (1.5) / 100 = 0.15
        const slippage = fromAmount * (slippagePercentage / 100);
        // fromAmount (10) + slippage (0.15) = 10.15
        const fromAmountPlusSlippage = fromAmount + slippage;
        return fromAmountPlusSlippage.toString();
    }
    async getEvmBalances({ userAddress, chains = [] }) {
        const chainRpcUrls = (0, evm_1.getChainRpcUrls)({
            chains: this.chains
        });
        const tokens = (0, evm_1.getEvmTokensForChainIds)({
            chainIds: chains,
            tokens: this.tokens
        });
        return this.handlers.evm.getBalances(tokens, userAddress, chainRpcUrls);
    }
    async getCosmosBalances({ addresses, chainIds = [] }) {
        const cosmosChains = (0, cosmos_1.getCosmosChainsForChainIds)({
            chainIds,
            chains: this.chains
        });
        return this.handlers.cosmos.getBalances({
            addresses,
            cosmosChains
        });
    }
    async getAllBalances({ chainIds = [], cosmosAddresses, evmAddress }) {
        const normalizedChainIds = chainIds.map(String);
        // fetch balances for provided chains
        const [evmChainIds, cosmosChainIds] = this.chains.reduce((cosmosAndEvmChains, chain) => {
            if (!normalizedChainIds.includes(String(chain.chainId))) {
                return cosmosAndEvmChains;
            }
            if (chain.chainType === types_1.ChainType.COSMOS) {
                cosmosAndEvmChains[1].push(chain.chainId);
            }
            else {
                cosmosAndEvmChains[0].push(chain.chainId);
            }
            return cosmosAndEvmChains;
        }, [[], []]);
        const evmBalances = evmAddress
            ? await this.getEvmBalances({
                chains: evmChainIds,
                userAddress: evmAddress
            })
            : [];
        const cosmosBalances = cosmosAddresses
            ? await this.getCosmosBalances({
                addresses: cosmosAddresses,
                chainIds: cosmosChainIds
            })
            : [];
        return {
            evmBalances,
            cosmosBalances
        };
    }
    // INTERNAL PRIVATES METHODS
    validateInit() {
        if (!this.initialized) {
            throw new Error("SquidSdk must be initialized! Please call the SquidSdk.init method");
        }
    }
    validateTransactionRequest(route) {
        if (!route.transactionRequest) {
            throw new Error("transactionRequest param not found in route object");
        }
    }
}
exports.Squid = Squid;
//# sourceMappingURL=index.js.map